// src/services/api.js
import apiRequest from "./api-core";
export const usersAPI = {
  // Get all users
  getAll: (skip = 0, limit = 100) => {
    return apiRequest(`/users?skip=${skip}&limit=${limit}`);
  },

  // Create new user
  create: (userData) => {
    return apiRequest("/users", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  },

  // Update user
  update: (id, userData) => {
    return apiRequest(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  },

  // Delete user
  delete: (id) => {
    return apiRequest(`/users/${id}`, {
      method: "DELETE",
    });
  },

  // Toggle user active status
  toggleActive: (id) => {
    return apiRequest(`/users/${id}/toggle-active`, {
      method: "PATCH",
    });
  },
};

// Patients API
export const patientsAPI = {
  getAll: (skip = 0, limit = 100, search = "") => {
    const params = new URLSearchParams();
    params.append("skip", skip.toString());
    params.append("limit", limit.toString());
    if (search) {
      params.append("search", search);
    }
    return apiRequest(`/patients?${params.toString()}`);
  },

  getById: (id) => {
    return apiRequest(`/patients/${id}`);
  },

  create: (patientData) => {
    return apiRequest("/patients", {
      method: "POST",
      body: JSON.stringify(patientData),
    });
  },

  update: (id, patientData) => {
    return apiRequest(`/patients/${id}`, {
      method: "PUT",
      body: JSON.stringify(patientData),
    });
  },

  delete: (id) => {
    return apiRequest(`/patients/${id}`, {
      method: "DELETE",
    });
  },

  search: (query, limit = 10) => {
    return apiRequest(
      `/patients/search?query=${encodeURIComponent(query)}&limit=${limit}`
    );
  },

  getAppointments: (patientId) => {
    return apiRequest(`/patients/${patientId}/appointments`);
  },

  sendVerificationCode: (patientId, method) => {
    return apiRequest(
      `/patients/${patientId}/send-verification?method=${method}`,
      {
        method: "POST",
      }
    );
  },

  verifyCode: (patientId, code) => {
    return apiRequest(`/patients/${patientId}/verify-code?code=${code}`, {
      method: "POST",
    });
  },

  verifyToken: (token) => {
    return apiRequest("/patients/verify-token", {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  },
};

// Appointments API
export const appointmentsAPI = {
  getAll: (
    skip = 0,
    limit = 100,
    dateFrom = null,
    dateTo = null,
    status = null,
    dentistId = null
  ) => {
    const params = new URLSearchParams();
    params.append("skip", skip.toString());
    params.append("limit", limit.toString());

    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (status) params.append("status", status);
    if (dentistId) params.append("dentist_id", dentistId.toString());

    return apiRequest(`/appointments?${params.toString()}`);
  },

  getById: (id) => {
    return apiRequest(`/appointments/${id}`);
  },

  create: (appointmentData) => {
    return apiRequest("/appointments", {
      method: "POST",
      body: JSON.stringify(appointmentData),
    });
  },

  update: (id, appointmentData) => {
    return apiRequest(`/appointments/${id}`, {
      method: "PUT",
      body: JSON.stringify(appointmentData),
    });
  },

  delete: (id) => {
    return apiRequest(`/appointments/${id}`, {
      method: "DELETE",
    });
  },

  getAvailableSlots: (dentistId, date) => {
    return apiRequest(
      `/appointments/available-slots/${dentistId}?appointment_date=${date}`
    );
  },

  getTodayAppointments: () => {
    return apiRequest("/appointments/today");
  },

  getUpcomingAppointments: (days = 7) => {
    return apiRequest(`/appointments/upcoming?days=${days}`);
  },

  updateStatus: (id, status) => {
    return apiRequest(`/appointments/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  },

  sendReminders: () => {
    return apiRequest("/appointments/send-reminders", {
      method: "POST",
    });
  },
};

// servicesAPI
export const servicesAPI = {
  getAll: (skip = 0, limit = 100) => {
    return apiRequest(`/services?skip=${skip}&limit=${limit}`);
  },

  getById: (id) => {
    return apiRequest(`/services/${id}`);
  },

  create: (serviceData) => {
    // Use JSON endpoint for creation
    return apiRequest("/services/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(serviceData),
    });
  },

  update: (id, serviceData) => {
    // Use PATCH for JSON data instead of PUT with FormData
    return apiRequest(`/services/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(serviceData),
    });
  },

  delete: (id) => {
    return apiRequest(`/services/${id}`, {
      method: "DELETE",
    });
  },

  // Image upload for services
  uploadServiceImage: (serviceId, file) => {
    const formData = new FormData();
    formData.append("image", file);

    return apiRequest(
      `/services/${serviceId}/upload-image`,
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },

  uploadFile: (file) => {
    const formData = new FormData();
    formData.append("file", file);

    return apiRequest(
      "/services/upload",
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },

  getByCategory: (category) => {
    return apiRequest(`/services/category/${category}`);
  },

  search: (query, skip = 0, limit = 100) => {
    return apiRequest(
      `/services/search/?query=${encodeURIComponent(
        query
      )}&skip=${skip}&limit=${limit}`
    );
  },

  toggleActive: (id) => {
    return apiRequest(`/services/${id}/toggle-active`, {
      method: "PATCH",
    });
  },
};

// staffAPI
export const staffAPI = {
  getAll: (skip = 0, limit = 100) => {
    return apiRequest(`/staff/?skip=${skip}&limit=${limit}`);
  },

  getById: (id) => {
    return apiRequest(`/staff/${id}`);
  },

  create: (formData) => {
    return apiRequest(
      "/staff/",
      {
        method: "POST",
        body: formData,
      },
      false
    ); // false = don't set Content-Type for FormData
  },

  update: (id, formData) => {
    return apiRequest(
      `/staff/${id}`,
      {
        method: "PUT",
        body: formData,
      },
      false
    );
  },

  delete: (id) => {
    return apiRequest(`/staff/${id}`, {
      method: "DELETE",
    });
  },

  uploadImage: (id, imageFile) => {
    const formData = new FormData();
    formData.append("image", imageFile);

    return apiRequest(
      `/staff/${id}/upload-image`,
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },
};

// Hero API
export const heroAPI = {
  getSlides: (isActive = null) => {
    const params = new URLSearchParams();
    if (isActive !== null) {
      params.append("is_active", isActive.toString());
    }
    const queryString = params.toString();
    return apiRequest(`/hero-slides${queryString ? `?${queryString}` : ""}`);
  },

  getSlide: (id) => {
    return apiRequest(`/hero-slides/${id}`);
  },

  createSlide: (formData) => {
    return apiRequest(
      "/hero-slides",
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },

  updateSlide: (id, formData) => {
    return apiRequest(
      `/hero-slides/${id}`,
      {
        method: "PUT",
        body: formData,
      },
      false
    );
  },

  deleteSlide: (id) => {
    return apiRequest(`/hero-slides/${id}`, {
      method: "DELETE",
    });
  },

  uploadImage: (id, imageFile) => {
    const formData = new FormData();
    formData.append("image", imageFile);

    return apiRequest(
      `/hero-slides/${id}/upload-image`,
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },
};

// Content API
export const contentAPI = {
  // Page Content (unchanged)
  getPages: () => {
    return apiRequest("/content/pages");
  },

  getPage: (slug) => {
    return apiRequest(`/content/pages/${slug}`);
  },

  createPage: (pageData) => {
    return apiRequest("/content/pages", {
      method: "POST",
      body: JSON.stringify(pageData),
    });
  },

  updatePage: (id, pageData) => {
    return apiRequest(`/content/pages/${id}`, {
      method: "PUT",
      body: JSON.stringify(pageData),
    });
  },

  deletePage: (id) => {
    return apiRequest(`/content/pages/${id}`, {
      method: "DELETE",
    });
  },

  // Blog Posts - Updated to use JSON
  getBlogPosts: (category = null) => {
    const url = category
      ? `/content/blog?category=${category}`
      : "/content/blog";
    return apiRequest(url);
  },

  getBlogPostBySlug: (slug) => {
    return apiRequest(`/content/blog/slug/${slug}`);
  },

  createBlogPost: (postData) => {
    return apiRequest("/content/blog", {
      method: "POST",
      body: JSON.stringify(postData),
    });
  },

  updateBlogPost: (id, postData) => {
    return apiRequest(`/content/blog/${id}`, {
      method: "PUT",
      body: JSON.stringify(postData),
    });
  },

  deleteBlogPost: (id) => {
    return apiRequest(`/content/blog/${id}`, {
      method: "DELETE",
    });
  },

  // File Upload (for Cloudinary)
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append("file", file);

    return apiRequest(
      "/content/upload",
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },

  // Additional blog endpoints
  uploadBlogImage: (postId, file) => {
    const formData = new FormData();
    formData.append("image", file);

    return apiRequest(
      `/content/blog/${postId}/upload-image`,
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },

  publishBlogPost: (postId) => {
    return apiRequest(`/content/blog/${postId}/publish`, {
      method: "PATCH",
    });
  },

  unpublishBlogPost: (postId) => {
    return apiRequest(`/content/blog/${postId}/unpublish`, {
      method: "PATCH",
    });
  },

  getBlogCategories: () => {
    return apiRequest("/content/blog/categories");
  },
};

// Testimonials API - Updated to send JSON data
export const testimonialsAPI = {
  // General testimonials (patients)
  getTestimonials: (type = null, approved = null) => {
    let url = `/testimonials/`;
    const params = new URLSearchParams();

    if (approved !== null) params.append("approved", approved);
    if (type) params.append("type", type);

    if (params.toString()) url += `?${params.toString()}`;
    return apiRequest(url);
  },

  getTestimonial: (id) => {
    return apiRequest(`/testimonials/${id}`);
  },

  createTestimonial: (testimonialData) => {
    // Send as JSON instead of FormData
    return apiRequest("/testimonials", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(testimonialData),
    });
  },

  updateTestimonial: (id, testimonialData) => {
    // Send as JSON instead of FormData
    return apiRequest(`/testimonials/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(testimonialData),
    });
  },

  deleteTestimonial: (id) => {
    return apiRequest(`/testimonials/${id}`, {
      method: "DELETE",
    });
  },

  // Student testimonials
  getStudentTestimonials: (featured = false, approved = true) => {
    const params = new URLSearchParams();
    params.append("approved", approved);
    if (featured) params.append("featured", featured);

    return apiRequest(`/testimonials/students/?${params.toString()}`);
  },

  getStudentTestimonial: (id) => {
    return apiRequest(`/testimonials/students/${id}`);
  },

  createStudentTestimonial: (testimonialData) => {
    // Send as JSON instead of FormData
    return apiRequest("/testimonials/students/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(testimonialData),
    });
  },

  updateStudentTestimonial: (id, testimonialData) => {
    // Send as JSON instead of FormData
    return apiRequest(`/testimonials/students/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(testimonialData),
    });
  },

  deleteStudentTestimonial: (id) => {
    return apiRequest(`/testimonials/students/${id}`, {
      method: "DELETE",
    });
  },

  // Image upload (still uses FormData for file upload)
  uploadTestimonialImage: (file) => {
    const formData = new FormData();
    formData.append("file", file);

    return apiRequest("/testimonials/upload-image", {
      method: "POST",
      body: formData,
    });
  },

  // Additional endpoints from backend
  approveTestimonial: (id) => {
    return apiRequest(`/testimonials/${id}/approve`, {
      method: "PATCH",
    });
  },

  approveStudentTestimonial: (id) => {
    return apiRequest(`/testimonials/students/${id}/approve`, {
      method: "PATCH",
    });
  },

  toggleFeaturedTestimonial: (id) => {
    return apiRequest(`/testimonials/students/${id}/feature`, {
      method: "PATCH",
    });
  },

  getTestimonialsByType: (type, approved = true, limit = 10) => {
    return apiRequest(
      `/testimonials/type/${type}?approved=${approved}&limit=${limit}`
    );
  },

  getFeaturedStudentTestimonials: (limit = 5) => {
    return apiRequest(`/testimonials/students/featured/?limit=${limit}`);
  },
};

// Settings API
export const settingsAPI = {
  getSettings: () => {
    return apiRequest("/settings");
  },

  updateSettings: (settingsData) => {
    return apiRequest("/settings", {
      method: "PUT",
      body: JSON.stringify(settingsData),
    });
  },
};

// About API with FormData support
export const aboutAPI = {
  // About Content
  getAboutContent: (isActive = null) => {
    const params = new URLSearchParams();
    if (isActive !== null) {
      params.append("is_active", isActive.toString());
    }
    const queryString = params.toString();
    return apiRequest(`/about/content${queryString ? `?${queryString}` : ""}`);
  },

  getAboutContentById: (id) => {
    return apiRequest(`/about/content/${id}`);
  },

  createAboutContent: (formData) => {
    return apiRequest(
      "/about/content",
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },

  updateAboutContent: (id, formData) => {
    return apiRequest(
      `/about/content/${id}`,
      {
        method: "PUT",
        body: formData,
      },
      false
    );
  },

  deleteAboutContent: (id) => {
    return apiRequest(`/about/content/${id}`, {
      method: "DELETE",
    });
  },

  // File Upload
  uploadFile: (file, subfolder = "general") => {
    const formData = new FormData();
    formData.append("file", file);

    return apiRequest(
      `/about/upload?subfolder=${subfolder}`,
      {
        method: "POST",
        body: formData,
      },
      false
    );
  },
};

// Training API
export const trainingAPI = {
  // Courses
  getCourses: (isActive = true) => {
    const params = new URLSearchParams();
    if (isActive !== null) {
      params.append("is_active", isActive.toString());
    }
    const queryString = params.toString();
    return apiRequest(
      `/training/courses${queryString ? `?${queryString}` : ""}`
    );
  },

  getCourse: (id) => {
    return apiRequest(`/training/courses/${id}`);
  },

  createCourse: (courseData) => {
    return apiRequest("/training/courses", {
      method: "POST",
      body: JSON.stringify(courseData),
    });
  },

  updateCourse: (id, courseData) => {
    return apiRequest(`/training/courses/${id}`, {
      method: "PUT",
      body: JSON.stringify(courseData),
    });
  },

  deleteCourse: (id) => {
    return apiRequest(`/training/courses/${id}`, {
      method: "DELETE",
    });
  },

  // Applications
  getApplications: (status = null) => {
    const params = new URLSearchParams();
    if (status) {
      params.append("status", status);
    }
    const queryString = params.toString();
    return apiRequest(
      `/training/applications${queryString ? `?${queryString}` : ""}`
    );
  },

  createApplication: (applicationData) => {
    return apiRequest("/training/applications", {
      method: "POST",
      body: JSON.stringify(applicationData),
    });
  },

  updateApplication: (id, applicationData) => {
    return apiRequest(`/training/applications/${id}`, {
      method: "PUT",
      body: JSON.stringify(applicationData),
    });
  },

  deleteApplication: (id) => {
    return apiRequest(`/training/applications/${id}`, {
      method: "DELETE",
    });
  },
};

// newsletter API
export const newsletterAPI = {
  // Subscribers
  getSubscribers: (skip = 0, limit = 100, isActive = null) => {
    let url = `/newsletter/subscribers?skip=${skip}&limit=${limit}`;
    if (isActive !== null) url += `&is_active=${isActive}`;
    return apiRequest(url);
  },

  subscribe: (subscriberData) => {
    return apiRequest("/newsletter/subscribers", {
      method: "POST",
      body: JSON.stringify(subscriberData),
    });
  },

  updateSubscriber: (id, subscriberData) => {
    return apiRequest(`/newsletter/subscribers/${id}`, {
      method: "PUT",
      body: JSON.stringify(subscriberData),
    });
  },

  unsubscribe: (email) => {
    return apiRequest(`/newsletter/subscribers/${email}/unsubscribe`, {
      method: "POST",
    });
  },

  deleteSubscriber: (id) => {
    return apiRequest(`/newsletter/subscribers/${id}`, {
      method: "DELETE",
    });
  },

  exportSubscribers: (format = "csv") => {
    return apiRequest(`/newsletter/subscribers/export?format=${format}`);
  },

  // Campaigns
  getCampaigns: (skip = 0, limit = 100, status = null) => {
    let url = `/newsletter/campaigns?skip=${skip}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    return apiRequest(url);
  },

  createCampaign: (campaignData) => {
    return apiRequest("/newsletter/campaigns", {
      method: "POST",
      body: JSON.stringify(campaignData),
    });
  },

  updateCampaign: (id, campaignData) => {
    return apiRequest(`/newsletter/campaigns/${id}`, {
      method: "PUT",
      body: JSON.stringify(campaignData),
    });
  },

  sendCampaign: (id) => {
    return apiRequest(`/newsletter/campaigns/${id}/send`, {
      method: "POST",
    });
  },

  duplicateCampaign: (id) => {
    return apiRequest(`/newsletter/campaigns/${id}/duplicate`, {
      method: "POST",
    });
  },

  getCampaignStats: (id) => {
    return apiRequest(`/newsletter/campaigns/${id}/stats`);
  },

  // Templates
  getTemplates: () => {
    return apiRequest("/newsletter/templates");
  },

  createTemplate: (templateData) => {
    return apiRequest("/newsletter/templates", {
      method: "POST",
      body: JSON.stringify(templateData),
    });
  },

  updateTemplate: (id, templateData) => {
    return apiRequest(`/newsletter/templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(templateData),
    });
  },

  deleteTemplate: (id) => {
    return apiRequest(`/newsletter/templates/${id}`, {
      method: "DELETE",
    });
  },

  // Analytics
  getStats: () => {
    return apiRequest("/newsletter/stats");
  },
};

// Contact API
export const contactAPI = {
  // Contact Messages
  getMessages: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (
        params[key] !== null &&
        params[key] !== undefined &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });
    return apiRequest(`/contact/messages?${queryParams.toString()}`);
  },

  getMessage: (id) => {
    return apiRequest(`/contact/messages/${id}`);
  },

  createMessage: (messageData) => {
    return apiRequest("/contact/messages", {
      method: "POST",
      body: JSON.stringify(messageData),
    });
  },

  updateMessage: (id, messageData) => {
    return apiRequest(`/contact/messages/${id}`, {
      method: "PUT",
      body: JSON.stringify(messageData),
    });
  },

  deleteMessage: (id) => {
    return apiRequest(`/contact/messages/${id}`, {
      method: "DELETE",
    });
  },

  archiveMessage: (id) => {
    return apiRequest(`/contact/messages/${id}/archive`, {
      method: "POST",
    });
  },

  replyToMessage: (id, replyData) => {
    return apiRequest(`/contact/messages/${id}/reply`, {
      method: "POST",
      body: JSON.stringify(replyData),
    });
  },

  // Email Templates
  getTemplates: () => {
    return apiRequest("/contact/templates");
  },

  createTemplate: (templateData) => {
    return apiRequest("/contact/templates", {
      method: "POST",
      body: JSON.stringify(templateData),
    });
  },

  updateTemplate: (id, templateData) => {
    return apiRequest(`/contact/templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(templateData),
    });
  },
};

// Analytics API
export const analyticsAPI = {
  // Dashboard Stats
  getDashboardStats: () => {
    return apiRequest("/analytics/dashboard");
  },

  // Revenue Analytics
  getRevenueAnalytics: (period = "month", startDate = null, endDate = null) => {
    let url = `/analytics/revenue?period=${period}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return apiRequest(url);
  },

  // Appointment Analytics
  getAppointmentAnalytics: (
    period = "month",
    startDate = null,
    endDate = null
  ) => {
    let url = `/analytics/appointments?period=${period}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return apiRequest(url);
  },

  // Patient Analytics
  getPatientAnalytics: (period = "month", startDate = null, endDate = null) => {
    let url = `/analytics/patients?period=${period}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return apiRequest(url);
  },

  // Report Generation
  generateReport: (reportData) => {
    return apiRequest("/analytics/reports/generate", {
      method: "POST",
      body: JSON.stringify(reportData),
    });
  },

  getReportStatus: (reportId) => {
    return apiRequest(`/analytics/reports/${reportId}`);
  },

  downloadReport: (reportId, format = "csv") => {
    return apiRequest(
      `/analytics/reports/${reportId}/download?format=${format}`
    );
  },

  // KPI Management
  getKPIs: () => {
    return apiRequest("/analytics/kpis");
  },

  createKPI: (kpiData) => {
    return apiRequest("/analytics/kpis", {
      method: "POST",
      body: JSON.stringify(kpiData),
    });
  },

  updateKPI: (id, kpiData) => {
    return apiRequest(`/analytics/kpis/${id}`, {
      method: "PUT",
      body: JSON.stringify(kpiData),
    });
  },

  // Event Tracking
  trackEvent: (eventData) => {
    return apiRequest("/analytics/events", {
      method: "POST",
      body: JSON.stringify(eventData),
    });
  },

  getEvents: (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== null && filters[key] !== undefined) {
        params.append(key, filters[key]);
      }
    });
    return apiRequest(`/analytics/events?${params.toString()}`);
  },
};

// AI Chat API
export const aiChatAPI = {
  sendMessage: (chatData) => {
    return apiRequest("/ai-chat/chat", {
      method: "POST",
      body: JSON.stringify(chatData),
    });
  },

  resetConversation: (conversationId) => {
    return apiRequest("/ai-chat/reset-conversation", {
      method: "POST",
      body: JSON.stringify({ conversation_id: conversationId }),
    });
  },

  getWelcomeMessage: () => {
    return apiRequest("/ai-chat/welcome-message");
  },
};
